import { useEffect, useState } from 'react';
import {
  useTheme,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Link,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { OfficialData, sendEmailInvites } from '../../../networkRequest/officialsService';
import { useUser } from '../../../hooks/useUser';
import EmailInviteChips from './EmailInviteChips';
import QrCodeIcon from '@mui/icons-material/QrCode';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { EmailIcon } from '../../../assets/icons/icons';
import googleStoreBadge from '../../../assets/googleStoreBadge.png';
import appleStoreBadge from '../../../assets/appleStoreBadge.png';
import SurePassLogoMark from '../../../assets/icons/SurePassLogoMarkContained';
import { Button } from '@baseComponents';

type EmailInviteProps = {
  open: boolean;
  onClose: () => void;
  onSubmit?: (recipients: OfficialData[]) => void;
  onError: (error: string) => void;
  initRecipients: OfficialData[];
};

export const EmailInviteDialog = ({
  open,
  onClose = () => { },
  onSubmit = () => { },
  onError,
  initRecipients
}: EmailInviteProps) => {
  const { user } = useUser();
  const theme = useTheme();
  const padding = theme.spacing(4);
  const [recipients, setRecipients] = useState<OfficialData[]>(initRecipients);

  useEffect(() => {
    setRecipients(initRecipients);
  }, [initRecipients]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!user) {
      throw new Error('User not found');
    }
    const response = await sendEmailInvites(
      user.accessToken,
      recipients.map((r) => r.id)
    );
    if (response.error) {
      onError(response.error);
    } else if (response.data) {
      onSubmit?.(recipients);
    }
  };

  const fieldStyle = {
    '& .MuiInputBase-input.Mui-disabled': {
      WebkitTextFillColor: theme.palette.text.primary,
      color: theme.palette.text.primary
    },
    '& .MuiInputLabel-root.Mui-disabled': {
      color: theme.palette.text.primary
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ height: '100%' }}
      slotProps={{ paper: { style: { borderRadius: 8 } } }}
    >
      <form onSubmit={handleSubmit} role="form">
        <DialogTitle sx={{ paddingX: padding, paddingTop: padding, paddingBottom: 2 }}>
          Email Invitations
        </DialogTitle>
        <DialogContent sx={{ padding }}>
          <Box sx={{
            mb: 3
          }}>
            {/* Recipients Field */}
            <EmailInviteChips
              initRecipients={initRecipients}
              onRecipientsChange={setRecipients}
              autoFocus
            />
          </Box>
          <Box sx={{
            mb: 3
          }}>
            {' '}
            {/* Subject Field */}
            <TextField
              fullWidth
              label="Subject"
              value="Welcome to SurePass – Easy and Secure Access Awaits! 🚀"
              disabled
              sx={fieldStyle}
            />
          </Box>
          <Box
            sx={{
              mb: 1,
              position: 'relative',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: '8px',
              mt: 2
            }}>
            <Typography
              component='span'
              sx={{
                position: 'absolute',
                top: 0,
                left: 12,
                transform: 'translateY(-50%)',
                bgcolor: 'background.paper',
                px: 0.5,
                fontSize: 12,
                color: 'text.secondary',
                lineHeight: 1,
              }}
            >
              Email Content
            </Typography>
            <Box sx={{ height: '50vh', overflow: 'scroll' }}>
              <Grid
                container
                size={12}
                spacing={2}
                sx={{
                  flexDirection: 'column',
                  alignItems: 'center',
                  marginTop: '24px',
                  marginBottom: '24px'
                }}>
                <SurePassLogoMark />
                <Typography variant="xxl" component="p">
                  Welcome to SurePass
                </Typography>
                <p>Download the SurePass App to your mobile device to get started. </p>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                  <Box
                    component="img"
                    src={googleStoreBadge}
                    alt="Google Play Store Badge"
                    sx={{ width: 'auto', height: 38, marginRight: 1 }}
                  />
                  <Box
                    component="img"
                    src={appleStoreBadge}
                    alt="Apple Store Badge"
                    sx={{ width: 'auto', height: 38 }}
                  />
                </Box>
                <Grid container sx={{
                  flexDirection: 'column',
                  alignItems: 'flex-start'
                }}>
                  <Typography variant='m'>1. Log in to the SurePass App</Typography>
                  <Typography variant='bodyMedium'>
                    Use your email <Typography component='span' sx={{ fontWeight: 700 }}>name@email.com</Typography> to view your Official Capacity Identification (OCID).
                  </Typography>
                  <Typography variant='m'>2. Scan Your OCID at SurePass Checkpoints</Typography>
                  <Typography variant='bodyMedium'>
                    There are 2 ways to scan your OCID at SurePass Checkpoints.
                  </Typography>
                  <Grid
                    container
                    sx={{
                      mt: '16px',
                      ml: '18px'
                    }}>
                    <QrCodeIcon />
                    <Box>
                      <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>QR Code</Typography>
                      <Typography variant='bodyMedium'>
                        SurePass checkpoints have a camera that will scan the QR Code on your OCID.
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid
                    container
                    sx={{
                      mt: '16px',
                      ml: '18px'
                    }}>
                    <AccountBalanceWalletIcon />
                    <Box>
                      <Typography variant='bodyMedium' sx={{ fontWeight: 500 }}>Mobile Wallet</Typography>
                      <Typography variant='bodyMedium'>
                        Add your OCID to your mobile wallet to scan at the checkpoint.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Typography variant='bodyMedium' sx={{
                  mt: '32px'
                }}>
                  <Link href='mailto:help@surepass.com'>Contact Support</Link> with questions or to request help at any time.
                </Typography>
                <Stack
                  direction='row'
                  sx={{
                    gap: 1,
                    mt: '16px'
                  }}>
                  <Typography variant='bodyMedium'>
                    <Link href='https://surepass.com/terms'>Terms of Service</Link>
                  </Typography>
                  <Typography variant='bodyMedium'>
                    <Link href='https://surepass.com/privacy/'>Privacy Policy</Link>
                  </Typography>
                  <Typography variant='bodyMedium'>
                    <Link href='mailto:help@surepass.com'>Contact Us</Link>
                  </Typography>
                </Stack>
              </Grid>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding, pt: 0, mr: 1 }}>
          <Button
            color='primary'
            mode='outlined'
            size='large'
            text='Discard'
            onClick={onClose}
          />
          <Button
            type='submit'
            size='large'
            text={`Send Invitation (${recipients.length})`}
            disabled={recipients.length === 0}
            startIcon={<EmailIcon sx={{ mr: 1 }} />}
          />
        </DialogActions>
      </form>
    </Dialog >
  );
};

export default EmailInviteDialog;
